"use client";

import type { Variants } from "framer-motion";
import { motion } from "framer-motion";
import React, { useState } from "react";
import { Parallax } from "react-scroll-parallax";

import FeedbackDoneInfo from "@/components/sections/Guestbook/FeedbackDoneInfo";
import FeedbackInfo from "@/components/sections/Guestbook/FeedbackInfo";
import CustomImage from "@/components/shared/CustomImage";
import { useTranslation } from "@/hooks/useTranslation";
import submitFeedbackForm from "@/lib/submitFeedbackForm";
import type { FeedbackData } from "@/types";

const itemVariants: Variants = {
  hidden: { y: 20, opacity: 0 },
  visible: {
    y: 0,
    opacity: 1,
    transition: {
      duration: 0.4,
      ease: "easeOut",
    },
  },
};

const initGuestbookForm: FeedbackData = {
  name: "",
  email: "",
  country: "",
  messages: "",
};

const MAX_MESSAGE_LENGTH = 500;

export default function FeedbackForm() {
  const [formData, setFormData] = useState<FeedbackData>(initGuestbookForm);
  const { t } = useTranslation();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const onInputChange = (newFormData: FeedbackData) => {
    // Limit messages field to MAX_MESSAGE_LENGTH characters
    if (
      newFormData.messages &&
      newFormData.messages.length > MAX_MESSAGE_LENGTH
    ) {
      const limitedData = {
        ...newFormData,
        messages: newFormData.messages.substring(0, MAX_MESSAGE_LENGTH),
      };
      setFormData(limitedData);
    } else {
      setFormData(newFormData);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate message length before submission
    if (formData.messages && formData.messages.length >= MAX_MESSAGE_LENGTH) {
      setSubmitError(
        t({
          en: `Message must be less than ${MAX_MESSAGE_LENGTH} characters.`,
          de: `Die Nachricht muss weniger als ${MAX_MESSAGE_LENGTH} Zeichen enthalten.`,
        })
      );
      return;
    }

    setIsSubmitting(true);
    setSubmitError("");

    const { success, messages } = await submitFeedbackForm(formData);

    try {
      if (success) {
        setSubmitSuccess(true);
      } else if (messages) setSubmitError(t(messages));
    } catch (error) {
      console.error("Form submission error details:", error);
      setSubmitError(
        t({
          en: "An error occurred, please try again later.",
          de: "Ein Fehler ist aufgetreten, bitte versuchen Sie es später erneut.",
        })
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  // Check if message length exceeds limit
  const messageCharacterCount = formData.messages?.length || 0;
  const isMessageLimitReached = messageCharacterCount >= MAX_MESSAGE_LENGTH;

  return (
    <div>
      <section id="contact" className="pt-0">
        <motion.div initial="hidden" animate="visible" variants={itemVariants}>
          {submitSuccess ? (
            <FeedbackDoneInfo
              backToFormAction={() => {
                setSubmitSuccess(false);
                setFormData(initGuestbookForm);
                setSubmitError("");
                setIsSubmitting(false);
              }}
            />
          ) : (
            <form
              onSubmit={handleSubmit}
              className="container mx-auto gap-6 overflow-hidden bg-white shadow-md lg:gap-0"
            >
              {submitError && (
                <div className="mb-4 bg-red-100 p-3 text-red-700">
                  {submitError}
                </div>
              )}
              <div className="grid grid-cols-1 lg:grid-cols-2">
                <div className="relative size-full min-h-72 overflow-hidden">
                  <Parallax
                    scale={[1, 2]}
                    className="absolute inset-0 size-full"
                  >
                    <CustomImage
                      src="/bg_maps.jpeg"
                      alt="Parallax Image"
                      fill
                      className="object-cover object-center"
                      priority
                    />
                  </Parallax>
                  <Parallax
                    opacity={[0, 2]}
                    className="absolute left-0 top-0 size-full bg-primary-800/85"
                  />
                  <Parallax
                    speed={-10}
                    className="container relative z-10 flex size-full items-center justify-center px-4"
                  >
                    <h2 className="text-center text-3xl font-bold !leading-normal text-white md:max-w-lg md:text-4xl">
                      {t({
                        de: "Wir freuen uns auf Ihre Nachricht!",
                        en: "We'd love to hear from you!",
                      })}
                    </h2>
                  </Parallax>
                </div>
                <div className="p-5 md:p-20 md:px-10">
                  <FeedbackInfo
                    formData={formData}
                    onInputChange={onInputChange}
                  />

                  <button
                    type="submit"
                    disabled={isSubmitting || isMessageLimitReached}
                    className={`mx-auto mt-6 flex w-fit items-center rounded-full px-8 py-4 text-lg font-bold text-white shadow-lg transition-colors duration-700 ${
                      isSubmitting || isMessageLimitReached
                        ? "cursor-not-allowed bg-primary-600 opacity-70"
                        : "bg-primary-600 hover:bg-primary-500"
                    }`}
                  >
                    {isSubmitting ? (
                      <>
                        <svg
                          className="-ml-1 mr-3 size-5 animate-spin text-white"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          ></circle>
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          ></path>
                        </svg>
                        {t({ de: "Senden...", en: "Sending..." })}
                      </>
                    ) : (
                      t({ de: "Senden", en: "Submit" })
                    )}
                  </button>
                </div>
              </div>
            </form>
          )}
        </motion.div>
      </section>
    </div>
  );
}
